<template>
  <!-- Signed-out marketing home. Owns its own nav + footer (App.vue hides the
       app chrome on this route), so it reads as a standalone landing page. -->
  <div class="landing">
    <!-- marketing nav -->
    <header class="mk-nav">
      <div class="mk-nav-inner">
        <router-link to="/" class="brand" aria-label="linker.to home">
          <span class="brand-mark"><span class="brand-diamond"></span></span>
          <span class="brand-name">linker<span class="brand-tld">.to</span></span>
        </router-link>
        <nav class="mk-links">
          <a href="#features" @click.prevent="scrollTo('features')">Product</a>
          <router-link to="/share">Files</router-link>
          <a href="#cta" @click.prevent="scrollTo('cta')">Pricing</a>
          <a :href="docsUrl" target="_blank" rel="noopener">Docs</a>
        </nav>
        <div class="mk-auth">
          <template v-if="isAuthenticated">
            <router-link to="/dashboard" class="mk-signup">Go to dashboard</router-link>
          </template>
          <template v-else>
            <router-link :to="{ name: 'Login' }" class="mk-login">Log in</router-link>
            <router-link :to="{ name: 'Login', query: { tab: 'signup' } }" class="mk-signup">Sign up free</router-link>
          </template>
        </div>
      </div>
    </header>

    <!-- hero -->
    <section class="hero">
      <div class="eyebrow-pill">Short links · files · pastes</div>
      <h1 class="hero-title">Every link, worth <span class="accent-italic">sharing.</span></h1>
      <p class="hero-sub">Shorten a URL, hand off a file, or drop a snippet — each one gets a clean page, a preview card you control, and an audit trail behind it.</p>

      <!-- link ⇄ file mode toggle -->
      <div class="mode-toggle" role="tablist" aria-label="What to share">
        <button class="mode-btn" :class="{ active: mode === 'link' }" role="tab" :aria-selected="mode === 'link'" @click="mode = 'link'">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"></path><path d="M15 7h2a5 5 0 1 1 0 10h-2"></path><line x1="8" y1="12" x2="16" y2="12"></line></svg>
          Short link
        </button>
        <button class="mode-btn" :class="{ active: mode === 'file' }" role="tab" :aria-selected="mode === 'file'" @click="mode = 'file'">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
          Share a file
        </button>
      </div>

      <!-- FILE MODE: drop a file, get a link (anonymous-friendly) -->
      <div v-if="mode === 'file'" class="file-wrap">
        <FileUploadComponent embedded />
      </div>

      <!-- LINK MODE: hero shorten bar (functional, anonymous-friendly) -->
      <template v-else-if="!linkResult">
        <form class="shorten-bar" :class="{ invalid: !!error }" @submit.prevent="shorten">
          <svg class="chain" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"></path><path d="M15 7h2a5 5 0 1 1 0 10h-2"></path><line x1="8" y1="12" x2="16" y2="12"></line></svg>
          <input
            v-model="linkInput"
            class="shorten-input mono"
            placeholder="Paste a long URL to shorten…"
            aria-label="Long URL to shorten"
            @input="error = ''"
          />
          <button class="shorten-btn" type="submit" :disabled="creating">
            <span v-if="creating" class="btn-spinner"></span>
            <span>{{ creating ? 'Shortening…' : 'Shorten →' }}</span>
          </button>
        </form>
        <div class="hero-fine" :class="{ err: !!error }">
          {{ error || 'No account needed to try · free tier forever' }}
        </div>
      </template>

      <!-- result -->
      <div v-else class="shorten-result">
        <div class="sr-head"><span class="sr-check">✓</span> Your short link is ready</div>
        <div class="sr-link">
          <span class="sr-short mono">{{ shortDisplayVal }}</span>
          <CopyButton :text="shortUrlVal" small />
        </div>
        <div class="sr-actions">
          <button class="link-btn" @click="reset">Shorten another</button>
          <router-link
            v-if="linkResult.claim_token && !isAuthenticated"
            class="link-btn"
            :to="{ name: 'Login', query: { redirect: `/dashboard/link/${linkResult.slug}?claim=1` } }"
          >Sign in to keep &amp; track it →</router-link>
          <router-link v-else class="link-btn" :to="`/dashboard/link/${linkResult.slug}`">View analytics →</router-link>
        </div>
      </div>
    </section>

    <!-- feature trio -->
    <section id="features" class="features">
      <div class="sec-head">
        <div class="sec-eyebrow">Three ways to share</div>
        <h2 class="sec-title">One tool, the whole handoff.</h2>
      </div>
      <div class="feature-grid">
        <div class="feature-card">
          <div class="feat-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"></path><path d="M15 7h2a5 5 0 1 1 0 10h-2"></path><line x1="8" y1="12" x2="16" y2="12"></line></svg>
          </div>
          <h3 class="feat-title">Short links</h3>
          <p class="feat-body">Custom slugs, expiry dates, password gates and click analytics — all on your own <span class="mono host">linker.to/</span> address.</p>
        </div>
        <div class="feature-card">
          <div class="feat-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
          </div>
          <h3 class="feat-title">File sharing</h3>
          <p class="feat-body">Drop a file, get a link. Inline previews, download caps and auto-expiry keep temporary handoffs actually temporary.</p>
        </div>
        <div class="feature-card">
          <div class="feat-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>
          </div>
          <h3 class="feat-title">Pastes &amp; auditing</h3>
          <p class="feat-body">Share snippets with syntax highlighting, and see every view in a full activity log — who, when, and from where.</p>
        </div>
      </div>
    </section>

    <!-- splash showcase -->
    <section class="showcase">
      <div class="showcase-panel">
        <div class="showcase-copy">
          <div class="sec-eyebrow">The page behind the link</div>
          <h2 class="showcase-title">Your links deserve a real preview.</h2>
          <p class="showcase-body">Set the title, description, image and accent that show up when your link is opened or pasted into a chat. Every share looks intentional — not like a random redirect.</p>
          <router-link to="/shorten" class="showcase-link">See preview options →</router-link>
        </div>
        <div class="showcase-sample">
          <!-- Editorial Ink treatment — deliberately bolder than the page: a
               preview of the public micro-site splash. -->
          <div class="sample-card">
            <div class="sample-bar"></div>
            <div class="sample-header">
              <span class="brand-name sm">linker<span class="brand-tld">.to</span></span>
              <span class="sample-tag mono">Shared&nbsp;Link</span>
            </div>
            <div class="sample-body">
              <div class="sample-eyebrow mono">— A link for you</div>
              <h3 class="sample-title">Q3 Product Roadmap</h3>
              <p class="sample-para">You're being taken to notion.so. Continue to view the destination.</p>
              <span class="sample-continue">Continue →</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- final CTA -->
    <section id="cta" class="cta-band">
      <h2 class="cta-title">Start sharing in seconds.</h2>
      <p class="cta-sub">Free forever for personal use. Team plans when you're ready to bring the audit trail to work.</p>
      <router-link :to="ctaRoute" class="cta-btn">Create your first link</router-link>
    </section>

    <!-- footer -->
    <footer class="mk-footer">
      <div class="mk-footer-inner">
        <span class="brand-name sm">linker<span class="brand-tld">.to</span></span>
        <nav class="mk-footer-links mono">
          <a href="#features" @click.prevent="scrollTo('features')">Product</a>
          <a href="#cta" @click.prevent="scrollTo('cta')">Pricing</a>
          <a :href="docsUrl" target="_blank" rel="noopener">Docs</a>
          <router-link to="/terms-of-service">Privacy</router-link>
          <a href="https://status.linker.to" target="_blank" rel="noopener">Status</a>
        </nav>
        <span class="mk-copy mono">© 2026 linker.to</span>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import CopyButton from '@/components/ui/CopyButton.vue';
import FileUploadComponent from '@/components/FileUploadComponent.vue';
import { shortUrl, shortDisplay, normalizeUrl } from '@/utils/links';
import { config } from '@/runtimeConfig';

export default {
  name: 'HomeView',
  components: { CopyButton, FileUploadComponent },
  data() {
    return {
      // Hero interaction: shorten a link ('link') or drop a file ('file').
      mode: 'link',
      linkInput: '',
      creating: false,
      error: '',
      linkResult: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    docsUrl() { return config.backendBaseUrl + 'docs/'; },
    shortUrlVal() { return this.linkResult ? shortUrl(this.linkResult.slug) : ''; },
    shortDisplayVal() { return this.linkResult ? shortDisplay(this.linkResult.slug) : ''; },
    ctaRoute() { return this.isAuthenticated ? '/dashboard' : '/shorten'; },
  },
  methods: {
    ...mapActions('shortLinks', ['createLink']),
    async shorten() {
      if (this.creating) return;
      this.error = '';
      const norm = normalizeUrl(this.linkInput);
      if (!norm.ok) { this.error = norm.error; return; }
      this.creating = true;
      try {
        this.linkResult = await this.createLink({ type: 'redirect', redirectTo: norm.url });
      } catch (e) {
        // store surfaces a toast on failure
      } finally {
        this.creating = false;
      }
    },
    reset() {
      this.linkResult = null;
      this.linkInput = '';
      this.error = '';
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  },
};
</script>

<style scoped>
/* Full-bleed page — override the app's fallback centering so bands run edge to
   edge; inner wrappers set their own max-widths. */
.landing {
  max-width: none;
  margin: 0;
  padding: 0;
  background: var(--bg);
  color: var(--text);
}

/* ---- shared brand mark ---- */
.brand { display: inline-flex; align-items: center; gap: 9px; text-decoration: none; }
.brand-mark {
  width: 26px; height: 26px;
  border-radius: 7px;
  background: var(--accent);
  display: inline-flex; align-items: center; justify-content: center;
}
.brand-diamond {
  width: 10px; height: 10px;
  border: 2px solid var(--bg);
  border-radius: 2.5px;
  transform: rotate(45deg);
}
.brand-name {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 23px;
  color: var(--text);
  line-height: 1;
}
.brand-name.sm { font-size: 18px; }
.brand-tld { color: var(--accent); }

/* ---- nav ---- */
.mk-nav { border-bottom: 1px solid var(--border); }
.mk-nav-inner {
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 20px clamp(24px, 5vw, 64px);
}
.mk-links {
  margin-left: 32px;
  display: flex;
  align-items: center;
  gap: 2px;
}
.mk-links a {
  font-size: 14px;
  font-weight: 500;
  color: var(--muted);
  padding: 8px 14px;
  text-decoration: none;
  border-radius: 5px;
  transition: color .15s, background .15s;
}
.mk-links a:hover { color: var(--text); background: var(--surface); }
.mk-auth {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 20px;
}
.mk-login {
  font-size: 14px;
  font-weight: 500;
  color: var(--text);
  text-decoration: none;
}
.mk-login:hover { color: var(--accent); }
.mk-signup {
  padding: 10px 20px;
  border-radius: 6px;
  background: var(--ink);
  color: var(--paper);
  font-weight: 600;
  font-size: 13.5px;
  text-decoration: none;
  transition: background .18s, color .18s;
}
.mk-signup:hover { background: var(--accent); color: var(--on-accent); }

/* ---- hero ---- */
.hero {
  text-align: center;
  max-width: 780px;
  margin: 0 auto;
  padding: 96px clamp(24px, 5vw, 64px) 72px;
  animation: lk-up .4s ease both;
}
.eyebrow-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--accent-h);
  background: var(--accent-soft);
  border: 1px solid color-mix(in srgb, var(--accent) 28%, var(--border));
  padding: 6px 13px;
  border-radius: 100px;
  margin-bottom: 28px;
}
.hero-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(48px, 6.4vw, 78px);
  line-height: 1.02;
  letter-spacing: -.015em;
  margin: 0 0 22px;
  color: var(--text);
}
.accent-italic { font-style: italic; color: var(--accent); }
.hero-sub {
  font-size: 19px;
  line-height: 1.6;
  color: var(--muted);
  max-width: 560px;
  margin: 0 auto 40px;
}

/* mode toggle */
.mode-toggle {
  display: inline-flex;
  gap: 4px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 100px;
  padding: 4px;
  margin: 0 auto 20px;
}
.mode-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .02em;
  color: var(--faint);
  background: transparent;
  border: none;
  padding: 8px 18px;
  border-radius: 100px;
  cursor: pointer;
  transition: background .18s, color .18s, box-shadow .18s;
}
.mode-btn:hover { color: var(--muted); }
.mode-btn.active {
  background: var(--field);
  color: var(--text);
  box-shadow: var(--shadow);
}
.mode-btn svg { flex-shrink: 0; }

/* file mode */
.file-wrap {
  max-width: 560px;
  margin: 0 auto 18px;
  text-align: left;
  animation: lk-pop .3s ease both;
}
.file-wrap :deep(.dropzone) {
  box-shadow: 0 14px 34px -18px rgba(28, 26, 21, .4);
}

/* shorten bar */
.shorten-bar {
  display: flex;
  align-items: center;
  background: var(--field);
  border: 1px solid var(--border-strong);
  border-radius: 8px;
  padding: 0 8px 0 18px;
  gap: 12px;
  max-width: 620px;
  margin: 0 auto 18px;
  box-shadow: 0 14px 34px -18px rgba(28, 26, 21, .4);
  transition: border-color .15s, box-shadow .15s;
}
.shorten-bar:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px var(--ring), 0 14px 34px -18px rgba(28, 26, 21, .4); }
.shorten-bar.invalid { border-color: var(--danger); }
.chain { color: var(--accent); flex-shrink: 0; }
.shorten-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 14.5px;
  padding: 17px 0;
}
.shorten-input::placeholder { color: var(--faint); }
.shorten-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 24px;
  border-radius: 6px;
  background: var(--ink);
  color: var(--paper);
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  font-family: var(--font-sans);
  white-space: nowrap;
  transition: background .18s, color .18s;
}
.shorten-btn:hover:not(:disabled) { background: var(--accent); color: var(--on-accent); }
.shorten-btn:disabled { opacity: .7; cursor: default; }
.btn-spinner {
  width: 14px; height: 14px;
  border: 2px solid color-mix(in srgb, var(--paper) 40%, transparent);
  border-top-color: var(--paper);
  border-radius: 50%;
  animation: lk-spin .7s linear infinite;
}
.hero-fine {
  font-family: var(--font-mono);
  font-size: 12.5px;
  color: var(--faint);
}
.hero-fine.err { color: var(--danger); }

/* hero result */
.shorten-result {
  max-width: 620px;
  margin: 0 auto 18px;
  background: var(--card);
  border: 1px solid var(--border-strong);
  border-radius: 8px;
  box-shadow: 0 14px 34px -18px rgba(28, 26, 21, .4);
  padding: 20px 22px;
  text-align: left;
  animation: lk-pop .35s ease both;
}
.sr-head {
  display: flex; align-items: center; gap: 8px;
  font-size: 12.5px; font-weight: 600; color: var(--success);
  margin-bottom: 12px;
}
.sr-check {
  width: 18px; height: 18px; border-radius: 50%;
  background: var(--success); color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 11px;
}
.sr-link {
  display: flex; align-items: center; gap: 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.sr-short { flex: 1; font-size: 16px; font-weight: 600; color: var(--accent); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sr-actions { display: flex; gap: 16px; flex-wrap: wrap; }
.link-btn {
  border: none; background: transparent;
  color: var(--muted); font-size: 13px; font-weight: 600;
  cursor: pointer; padding: 0; text-decoration: none;
}
.link-btn:hover { color: var(--accent); }

/* ---- section heading block ---- */
.sec-eyebrow {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--faint);
  margin-bottom: 10px;
}
.sec-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(34px, 4vw, 44px);
  line-height: 1.05;
  margin: 0;
  color: var(--text);
}

/* ---- feature trio ---- */
.features {
  max-width: 1160px;
  margin: 0 auto;
  padding: 56px clamp(24px, 5vw, 64px) 20px;
}
.features .sec-head { text-align: center; margin-bottom: 44px; }
.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}
.feature-card {
  background: var(--card-alt);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 30px 28px;
}
.feat-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  background: var(--accent-soft);
  border: 1px solid var(--border);
  display: inline-flex; align-items: center; justify-content: center;
  color: var(--accent);
  margin-bottom: 20px;
}
.feat-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 25px;
  line-height: 1.1;
  margin: 0 0 10px;
  color: var(--text);
}
.feat-body { font-size: 14.5px; line-height: 1.55; color: var(--muted); margin: 0; }
.host { font-size: 13px; color: var(--accent-h); }

/* ---- showcase ---- */
.showcase {
  max-width: 1160px;
  margin: 0 auto;
  padding: 52px clamp(24px, 5vw, 64px) 72px;
}
.showcase-panel {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: clamp(28px, 4vw, 52px);
  display: flex;
  gap: 48px;
  align-items: center;
  flex-wrap: wrap;
}
.showcase-copy { flex: 1; min-width: 280px; }
.showcase-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(30px, 3.4vw, 36px);
  line-height: 1.08;
  margin: 0 0 16px;
  color: var(--text);
}
.showcase-body { font-size: 15.5px; line-height: 1.6; color: var(--muted); margin: 0 0 24px; }
.showcase-link {
  font-size: 14.5px;
  font-weight: 600;
  color: var(--text);
  border-bottom: 1.5px solid var(--accent);
  padding-bottom: 2px;
  text-decoration: none;
}
.showcase-link:hover { color: var(--accent); }
.showcase-sample { flex: 1; min-width: 300px; display: flex; justify-content: center; }

/* Editorial Ink sample card */
.sample-card {
  width: 100%;
  max-width: 380px;
  background: var(--card);
  border: 1px solid var(--ink);
  border-radius: 2px;
  box-shadow: 6px 6px 0 color-mix(in srgb, var(--ink) 88%, transparent);
  overflow: hidden;
}
.sample-bar { height: 6px; background: var(--accent); }
.sample-header {
  padding: 14px 24px;
  display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid var(--ink);
}
.sample-tag {
  font-size: 9.5px; font-weight: 600;
  letter-spacing: .18em; text-transform: uppercase;
  color: var(--faint);
}
.sample-body { padding: 28px 24px; }
.sample-eyebrow {
  font-size: 9.5px; font-weight: 600;
  letter-spacing: .2em; text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 12px;
}
.sample-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 2rem;
  margin: 0 0 10px;
  line-height: 1.02;
  color: var(--text);
}
.sample-para { color: var(--muted); margin: 0 0 22px; font-size: .9rem; line-height: 1.5; }
.sample-continue {
  padding: 12px 24px;
  font-size: .9rem; font-weight: 600;
  background: var(--ink); color: var(--paper);
  border-radius: 2px;
  display: inline-block;
}

/* ---- final CTA ---- */
.cta-band {
  background: var(--ink);
  padding: clamp(56px, 7vw, 80px) clamp(24px, 5vw, 64px);
  text-align: center;
}
.cta-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(36px, 4.6vw, 54px);
  line-height: 1.05;
  margin: 0 0 16px;
  color: var(--paper);
}
.cta-sub {
  font-size: 16.5px;
  line-height: 1.6;
  color: color-mix(in srgb, var(--paper) 62%, transparent);
  max-width: 480px;
  margin: 0 auto 32px;
}
.cta-btn {
  padding: 14px 28px;
  border-radius: 7px;
  background: var(--accent);
  color: var(--on-accent);
  font-weight: 600;
  font-size: 15px;
  display: inline-block;
  text-decoration: none;
  transition: filter .18s;
}
.cta-btn:hover { filter: brightness(1.06); }

/* ---- footer ---- */
.mk-footer { background: var(--surface); border-top: 1px solid var(--border-strong); }
.mk-footer-inner {
  max-width: 1240px;
  margin: 0 auto;
  padding: 28px clamp(24px, 5vw, 64px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.mk-footer-links { display: flex; gap: 22px; }
.mk-footer-links a { font-size: 12px; color: var(--faint); text-decoration: none; }
.mk-footer-links a:hover { color: var(--text); }
.mk-copy { font-size: 11.5px; color: var(--faint); }

/* ---- responsive ---- */
@media (max-width: 720px) {
  .mk-nav-inner { flex-wrap: wrap; gap: 12px; }
  .mk-links { margin-left: 0; order: 3; width: 100%; justify-content: center; flex-wrap: wrap; }
  .mk-auth { margin-left: auto; }
  .hero { padding-top: 60px; }
}
@media (max-width: 520px) {
  .shorten-bar { flex-wrap: wrap; padding: 10px; gap: 8px; }
  .shorten-input { flex-basis: 100%; padding: 8px 4px; }
  .shorten-btn { flex: 1; justify-content: center; }
}
</style>
